/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text, Box, ActionIcon, Anchor, InstagramLogoIcon, LinkedInLogoIcon } from '@/shared/ui';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/ui/utils/tailwind';

const LEGAL_LINKS = [
  { key: 'privacy', href: '/privacy/latest' },
  { key: 'terms', href: '/serviceterms/latest' },
  { key: 'cookies', href: '/cookies/latest' },
] as const;

const Footer: React.FC<{ id?: string }> = ({ id }) => {
  const { t } = useTranslation('@pages/landing');

  const currentYear = new Date().getFullYear();

  return (
    <Box
      id={id}
      component="footer"
      className={cn(
        'w-full bg-(--surface-primary) py-(--space-6) text-(--text-secondary)',
        'max-md:px-(--space-3) max-md:py-(--space-3)',
      )}
    >
      <div className={cn(
        'flex items-center gap-(--space-4) mx-[25px]',
        'max-md:flex-col max-md:items-center',
      )}>
        <Text className="text-(--text-secondary) text-(length:--text-small) leading-(--leading-normal)">
          © {currentYear} {t('footer.brandPrefix')}{' '}
          <Anchor
            href="https://www.k4d.com.br"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            className="text-(--text-secondary) text-(length:--text-small) leading-(--leading-normal)"
          >
            {t('footer.brandK4D')}
          </Anchor>
          . {t('footer.allRightsReserved')}.
        </Text>

        <nav
          className={cn(
            'flex gap-(--space-3) flex-wrap justify-start',
            'max-md:justify-center max-md:pb-(--space-6)',
          )}
          aria-label={t('footer.legalLinksLabel')}
        >
          {LEGAL_LINKS.map(({ key, href }) => (
            <Anchor
              key={key}
              href={href}
              className={cn(
                'font-inherit text-(length:--text-small) font-(--font-regular)',
                'text-(--text-tertiary) py-(--space-1) no-underline',
                'transition-colors duration-150 ease-in-out',
                'hover:text-(--text-primary) hover:underline',
              )}
              underline="hover"
            >
              {t(`footer.${key}`)}
            </Anchor>
          ))}
        </nav>

        <div className="flex gap-(--space-2) items-center ml-auto">
          <ActionIcon
            component="a"
            href="https://www.linkedin.com/showcase/housien"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            size="md"
            aria-label={t('footer.linkedinLabel')}
          >
            <LinkedInLogoIcon className="w-5 h-5 text-(--text-tertiary) transition-colors duration-150 ease-in-out hover:text-(--text-primary)" />
          </ActionIcon>
          <ActionIcon
            component="a"
            href="https://www.instagram.com/housien.br"
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            size="md"
            aria-label={t('footer.instagramLabel')}
          >
            <InstagramLogoIcon className="w-5 h-5 text-(--text-tertiary) transition-colors duration-150 ease-in-out hover:text-(--text-primary)" />
          </ActionIcon>
        </div>
      </div>
    </Box>
  );
};

export default Footer;
